/**
 * @fileoverview Present recorded application activity by actor, feature, action, and outcome. The backend owns event capture, filtering, and bounded pagination; this workspace owns draft/applied filters and inspection. Audit evidence covers instrumented behavior rather than all activity. `AiSpendDashboard.js` separately owns provider billing and AI cost attribution.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'

import api from '../../api'
import { AppButton } from '../shared'
import { AdminEmptyState, AdminNote, AdminPage, AdminSection } from './AdminChrome'
import './UsageAuditDashboard.css'


const PAGE_SIZE = 50



function dateInputValue(date) {
    return date.toISOString().slice(0, 10)
}



function initialFilters() {
    const end = new Date()
    const start = new Date(end)
    start.setDate(start.getDate() - 30)
    return {
        start: dateInputValue(start),
        end: dateInputValue(end),
        feature: '',
        action: '',
        actor: '',
        outcome: '',
        q: '',
    }
}



function formatNumber(value) {
    return Number(value || 0).toLocaleString()
}



function formatDuration(value) {
    const milliseconds = Number(value || 0)
    if (milliseconds < 1000) return `${formatNumber(milliseconds)} ms`
    return `${(milliseconds / 1000).toFixed(milliseconds < 10000 ? 1 : 0)} s`
}



function formatTimestamp(value) {
    if (!value) return '—'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    }).format(date)
}



function labelFromKey(value) {
    return String(value || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
        .replace(/\bId\b/g, 'ID')
}



function actorLabel(actor = {}) {
    return actor.name || actor.email || (actor.user_id ? `User ${actor.user_id}` : 'System')
}



function choiceSummary(event) {
    const entries = Object.entries(event.input_choices || {})
    if (!entries.length) return '—'
    return entries.slice(0, 3).map(([key, value]) => `${labelFromKey(key)}: ${String(value)}`).join(' · ')
}



function identifierSummary(event) {
    const entries = Object.entries(event.entity_ids || {})
    if (!entries.length) return '—'
    return entries.map(([key, value]) => `${labelFromKey(key)} ${String(value)}`).join(' · ')
}



function AuditSection({ className = '', title, description, actions, children }) {
    return (
        <section className={`usage-audit-panel ${className}`.trim()}>
            <header className="usage-audit-panel-header">
                <div>
                    <h3>{title}</h3>
                    {description ? <p>{description}</p> : null}
                </div>
                {actions ? <div className="usage-audit-panel-actions">{actions}</div> : null}
            </header>
            {children}
        </section>
    )
}



function UsageAuditDashboard({ embedded = false }) {
    const [draftFilters, setDraftFilters] = useState(initialFilters)
    const [filters, setFilters] = useState(initialFilters)
    const [page, setPage] = useState(1)
    const [dashboard, setDashboard] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')

    const loadDashboard = useCallback(

    async () => {
        setLoading(true)
        setError('')
        try {
            const response = await api.get('/admin/audit-dashboard', {
                params: {
                    ...filters,
                    page,
                    page_size: PAGE_SIZE,
                },
            })
            setDashboard(response.data)
        } catch (requestError) {
            console.error('Failed to load usage audit dashboard:', requestError)
            setError(requestError.response?.data?.error || 'Usage audit dashboard failed to load.')
        } finally {
            setLoading(false)
        }
    }, [filters, page])

    useEffect(() => {
        loadDashboard()
    }, [loadDashboard])

    const summary = dashboard?.summary || {}
    const pagination = dashboard?.pagination || { page: 1, page_size: PAGE_SIZE, total: 0 }
    const totalPages = Math.max(1, Math.ceil((pagination.total || 0) / (pagination.page_size || PAGE_SIZE)))
    const actorOptions = useMemo(() => {
        const seen = new Set()
        return (dashboard?.people || []).filter(

        (person) => {
            const value = person.user_id || person.email || String(person.employee_id || '')
            if (!value || seen.has(value)) return false
            seen.add(value)
            return true
        }).map((person) => ({
            value: person.user_id || person.email || String(person.employee_id),
            label: person.name || person.email || `User ${person.user_id}`,
        }))
    }, [dashboard])



    function updateDraft(key, value) {
        setDraftFilters((current) => ({ ...current, [key]: value }))
    }



    function applyFilters(event) {
        event.preventDefault()
        setPage(1)
        setFilters({ ...draftFilters })
    }



    function clearFilters() {
        const next = initialFilters()
        setDraftFilters(next)
        setFilters(next)
        setPage(1)
    }

    const Wrapper = embedded ? AdminSection : AdminPage

    return (
        <Wrapper
            eyebrow="Operations"
            title="Activity audit"
            description="Adoption, failures, and structured employee activity across Documents, Intake, Matters, Tasks, and timekeeping."
            actions={(
                <AppButton
                    variant="secondary"
                    icon="pi pi-refresh"
                    label={loading ? 'Refreshing…' : 'Refresh'}
                    disabled={loading}
                    onClick={loadDashboard}
                />
            )}
        >
            {error ? <AdminNote tone="warning" title="Dashboard unavailable">{error}</AdminNote> : null}

            <div className="usage-audit-workspace">
                <form className="usage-audit-filters" onSubmit={applyFilters}>
                    <h3>Filter activity</h3>
                    <label>
                        <span>From</span>
                        <input type="date" value={draftFilters.start} onChange={(event) => updateDraft('start', event.target.value)} />
                    </label>
                    <label>
                        <span>To</span>
                        <input type="date" value={draftFilters.end} onChange={(event) => updateDraft('end', event.target.value)} />
                    </label>
                    <label>
                        <span>Feature</span>
                        <select value={draftFilters.feature} onChange={(event) => updateDraft('feature', event.target.value)}>
                            <option value="">All features</option>
                            {(dashboard?.options?.features || []).map((feature) => <option key={feature} value={feature}>{feature}</option>)}
                        </select>
                    </label>
                    <label>
                        <span>Action</span>
                        <select value={draftFilters.action} onChange={(event) => updateDraft('action', event.target.value)}>
                            <option value="">All actions</option>
                            {(dashboard?.options?.actions || []).map((action) => <option key={action} value={action}>{labelFromKey(action)}</option>)}
                        </select>
                    </label>
                    <label>
                        <span>Person</span>
                        <select value={draftFilters.actor} onChange={(event) => updateDraft('actor', event.target.value)}>
                            <option value="">All people</option>
                            {actorOptions.map((person) => <option key={person.value} value={person.value}>{person.label}</option>)}
                        </select>
                    </label>
                    <label>
                        <span>Outcome</span>
                        <select value={draftFilters.outcome} onChange={(event) => updateDraft('outcome', event.target.value)}>
                            <option value="">All outcomes</option>
                            <option value="success">Success</option>
                            <option value="failure">Failure</option>
                        </select>
                    </label>
                    <label className="usage-audit-search">
                        <span>Search</span>
                        <input
                            type="search"
                            value={draftFilters.q}
                            placeholder="Person, route, matter, lead, document…"
                            onChange={(event) => updateDraft('q', event.target.value)}
                        />
                    </label>
                    <div className="usage-audit-filter-actions">
                        <AppButton type="submit" icon="pi pi-filter" label="Apply filters" />
                        <AppButton type="button" variant="secondary" label="Clear" onClick={clearFilters} />
                    </div>
                </form>

                <section className="usage-audit-summary" aria-labelledby="usage-audit-summary-title">
                    <h3 id="usage-audit-summary-title">Range summary</h3>
                    <div className="usage-audit-metrics" aria-label="Usage summary">
                        <div className="usage-audit-metric usage-audit-metric-events">
                            <span>Events</span>
                            <strong>{formatNumber(summary.events)}</strong>
                            <small>Recorded in range</small>
                        </div>
                        <div className="usage-audit-metric usage-audit-metric-people">
                            <span>Active people</span>
                            <strong>{formatNumber(summary.active_people)}</strong>
                            <small>Identified employees</small>
                        </div>
                        <div className="usage-audit-metric usage-audit-metric-failures">
                            <span>Failure rate</span>
                            <strong>{Number(summary.failure_rate || 0).toFixed(1)}%</strong>
                            <small>{formatNumber(summary.failures)} failed events</small>
                        </div>
                        <div className="usage-audit-metric usage-audit-metric-duration">
                            <span>Average duration</span>
                            <strong>{formatDuration(summary.average_duration_ms)}</strong>
                            <small>Request elapsed time</small>
                        </div>
                    </div>
                </section>

                <div className="usage-audit-split">
                    <AuditSection title="Feature & action" description="Volume, reliability, and response time.">
                        <div className="usage-audit-table-wrap">
                            <table className="usage-audit-table">
                                <thead>
                                    <tr>
                                        <th>Feature</th>
                                        <th>Action</th>
                                        <th>Events</th>
                                        <th>Failure</th>
                                        <th>Avg. time</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(dashboard?.features || []).map((row) => (
                                        <tr key={`${row.feature}:${row.action}`}>
                                            <td><span className={`usage-audit-feature usage-audit-feature-${row.feature.toLowerCase()}`}>{row.feature}</span></td>
                                            <td>{labelFromKey(row.action)}</td>
                                            <td>{formatNumber(row.events)}</td>
                                            <td className={row.failures ? 'usage-audit-danger' : ''}>{Number(row.failure_rate || 0).toFixed(1)}%</td>
                                            <td>{formatDuration(row.average_duration_ms)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {!loading && !(dashboard?.features || []).length ? (
                                <div className="usage-audit-empty-wrap"><AdminEmptyState title="No feature activity" description="No events match this range and filter set." /></div>
                            ) : null}
                        </div>
                    </AuditSection>

                    <AuditSection title="People" description="Activity and failures by employee.">
                        <div className="usage-audit-table-wrap">
                            <table className="usage-audit-table">
                                <thead>
                                    <tr>
                                        <th>Person</th>
                                        <th>Events</th>
                                        <th>Failures</th>
                                        <th>Last seen</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(dashboard?.people || []).map((person) => (
                                        <tr key={`${person.user_id || person.email || 'system'}:${person.employee_id || ''}`}>
                                            <td>
                                                <div className="usage-audit-person">
                                                    <span className="usage-audit-avatar" aria-hidden="true">{(person.name || person.email || 'S').charAt(0).toUpperCase()}</span>
                                                    <span>
                                                        <strong>{person.name || person.email || 'System'}</strong>
                                                        {person.name && person.email ? <small>{person.email}</small> : null}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>{formatNumber(person.events)}</td>
                                            <td className={person.failures ? 'usage-audit-danger' : ''}>{formatNumber(person.failures)}</td>
                                            <td>{formatTimestamp(person.last_seen_at)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {!loading && !(dashboard?.people || []).length ? (
                                <div className="usage-audit-empty-wrap"><AdminEmptyState title="No employee activity" description="No identified people match these filters." /></div>
                            ) : null}
                        </div>
                    </AuditSection>
                </div>

                <AuditSection
                    className="usage-audit-history-panel"
                    title="Event history"
                    description={`${formatNumber(pagination.total)} matching event${pagination.total === 1 ? '' : 's'}.`}
                    actions={<span className="usage-audit-page-label">Page {pagination.page || 1} of {totalPages}</span>}
                >
                    <div className="usage-audit-table-wrap">
                        <table className="usage-audit-table usage-audit-history">
                            <thead>
                                <tr>
                                    <th>When</th>
                                    <th>Person</th>
                                    <th>Feature / action</th>
                                    <th>Related record</th>
                                    <th>Choices</th>
                                    <th>Outcome</th>
                                    <th>Time</th>
                                    <th aria-label="Details"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {(dashboard?.events || []).map((event) => (
                                    <tr key={event.id}>
                                        <td className="usage-audit-nowrap">{formatTimestamp(event.occurred_at)}</td>
                                        <td>{actorLabel(event.actor)}</td>
                                        <td>
                                            <strong>{event.feature}</strong>
                                            <small className="usage-audit-block">{labelFromKey(event.action)}</small>
                                        </td>
                                        <td>{identifierSummary(event)}</td>
                                        <td>{choiceSummary(event)}</td>
                                        <td>
                                            <span className={`usage-audit-outcome usage-audit-outcome-${event.outcome}`}>
                                                <i className={`pi ${event.outcome === 'success' ? 'pi-check-circle' : 'pi-exclamation-circle'}`} aria-hidden="true" />
                                                {labelFromKey(event.outcome)}
                                            </span>
                                        </td>
                                        <td>{formatDuration(event.duration_ms)}</td>
                                        <td>
                                            <details className="usage-audit-details">
                                                <summary aria-label={`Details for event ${event.id}`}><i className="pi pi-ellipsis-h" aria-hidden="true" /></summary>
                                                <div>
                                                    <dl>
                                                        <dt>Route</dt><dd><code>{event.http_method} {event.route}</code></dd>
                                                        <dt>Correlation</dt><dd><code>{event.correlation_id}</code></dd>
                                                        {event.handoff_id ? <><dt>Handoff</dt><dd><code>{event.handoff_id}</code></dd></> : null}
                                                        {event.error ? <><dt>Error</dt><dd>{event.error.code}: {event.error.message}</dd></> : null}
                                                    </dl>
                                                </div>
                                            </details>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {!loading && !(dashboard?.events || []).length ? (
                            <div className="usage-audit-empty-wrap"><AdminEmptyState title="No audit events" description="Try a broader date range or clear one of the filters." /></div>
                        ) : null}
                    </div>
                    <div className="usage-audit-pagination">
                        <AppButton variant="secondary" icon="pi pi-chevron-left" label="Previous" disabled={page <= 1 || loading} onClick={() => setPage((value) => Math.max(1, value - 1))} />
                        <AppButton variant="secondary" icon="pi pi-chevron-right" label="Next" disabled={page >= totalPages || loading} onClick={() => setPage((value) => value + 1)} />
                    </div>
                </AuditSection>
            </div>
        </Wrapper>
    )
}

export default UsageAuditDashboard
